import { useEnvironment } from '@/hooks/useEnvironment';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalVmClient } from '@/api/context';
import { useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBalance } from '@/hooks/useBalance';
import type { UsageInfo } from '@/api/types';
import { cn, isSymbolicaUser } from '@/lib/utils';
import {
  isPaperThemeActive,
  usePaperThemeActive,
  setPaperTheme,
  usePaperThemePreference,
  type ThemePreference,
} from '@/hooks/useThemeFlag';
import {
  useDevBannerOverrides,
  setDevBannerOverride,
  type DevBannerKey,
} from '@/lib/devBannerTriggers';
import {
  useDevPanelSplit,
  setDevPanelSplit,
  DEV_PANEL_SPLIT_PRESETS,
} from '@/lib/devPanelSplit';
import { useDevPanelOpen, toggleDevPanelOpen, setDevPanelOpen } from '@/lib/devPanelOpen';
import { WindowedCard, type WindowedCardMode } from '@/components/window/WindowedCard';
import {
  useDevComponentPreview,
  setDevComponentPreview,
  type DevComponentPreviewKey,
} from '@/lib/devComponentPreview';
import { DevComponentPreviewHost } from '@/components/DevComponentPreviewHost';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_STROKE_WIDTH,
} from '@/components/paper/paperShapes';
import { Wrench } from 'lucide-react';

type SessionUsage = UsageInfo;
const DEV_TRIGGER_BUTTON_BASE = `${PAPER_CONTROL_RADIUS_CLASS} border px-1.5 py-0.5 text-[10px] transition-colors`;

const useIsDevPanelEnabled = (): boolean => {
  const auth = useAuth();
  return Boolean(import.meta.env.VITE_ENABLE_DEV_PANEL) && isSymbolicaUser(auth.user?.email);
};

export const ConditionalDevPanel = () => {
  const enabled = useIsDevPanelEnabled();
  if (!enabled) return null;
  return <DevPanel />;
};

/** Rail-docked toggle button. Only renders when the dev panel is enabled
 *  and the paper theme is active. Pass as `<SidebarRail bottomSlot={...} />`. */
export const ConditionalRailDevToggle = ({ expanded = false }: { expanded?: boolean }) => {
  const enabled = useIsDevPanelEnabled();
  const open = useDevPanelOpen();
  const paper = usePaperThemeActive();
  if (!enabled || !paper) return null;
  return (
    <button
      type="button"
      aria-label={open ? 'Close developer console' : 'Open developer console'}
      title={expanded ? undefined : 'Developer Console'}
      onClick={toggleDevPanelOpen}
      className={cn(
        'flex items-center gap-[10px] transition-colors duration-150',
        PAPER_CONTROL_RADIUS_CLASS,
        'text-[12px] whitespace-nowrap',
        expanded ? 'h-[32px] px-[10px] w-full justify-start' : 'h-[32px] w-[32px] justify-center',
        open
          ? 'text-[var(--periwinkle)] bg-[var(--periwinkle-wash)]'
          : 'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)]',
      )}
    >
      <Wrench className="w-[20px] h-[20px] shrink-0" strokeWidth={1.6} />
      {expanded && <span>Developer Console</span>}
    </button>
  );
};

export const ConditionalMobileDevToggle = () => {
  const enabled = useIsDevPanelEnabled();
  const open = useDevPanelOpen();
  const paper = usePaperThemeActive();
  if (!enabled || !paper) return null;
  return (
    <button
      type="button"
      aria-label={open ? 'Close developer console' : 'Open developer console'}
      aria-pressed={open}
      onClick={toggleDevPanelOpen}
      className={cn(
        'w-full relative flex flex-col items-center justify-center gap-[2px]',
        'border-0 bg-transparent cursor-pointer p-0',
        'transition-colors duration-150',
        open ? 'text-[var(--fg)]' : 'text-[var(--fg-4)] hover:text-[var(--fg-2)]',
      )}
    >
      {open && (
        <span
          aria-hidden
          className="absolute top-0 left-[24px] right-[24px] h-[2px] rounded-full bg-[var(--fg)]"
        />
      )}
      <span className="inline-flex items-center justify-center w-[24px] h-[24px]">
        <Wrench className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
      </span>
      <span className={cn('text-[10px] tracking-[0.04em]', open ? 'font-medium' : 'font-normal')}>
        Dev
      </span>
    </button>
  );
};

export const DevPanel = () => {
  const env = useEnvironment();
  const auth = useAuth();
  const { remainingBalance, loading: balanceLoading, error: balanceError } = useBalance();
  const vmClient = useOptionalVmClient();
  interface ServerInfo {
    model: string;
    commit: string;
  }

  const [serverInfo, setServerInfo] = useState<ServerInfo>({ model: 'loading…', commit: 'loading…' });
  const [sessionUsage, setSessionUsage] = useState<SessionUsage | null>(null);
  const costIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!vmClient) return;

    const load = async () => {
      const model = await vmClient.fetchModels().then((d) => d.default || '—').catch(() => 'unavailable');
      const commit = await vmClient.fetchStatus().then((d) => d.commit_hash || 'unknown').catch(() => 'unavailable');
      if (!cancelled) setServerInfo({ model, commit });
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [vmClient]);

  useEffect(() => {
    let cancelled = false;
    if (!vmClient) return;

    const fetchCost = async () => {
      try {
        const sessions = await vmClient.listSessions(1, 0);
        if (!cancelled && sessions[0]?.usage) {
          setSessionUsage(sessions[0].usage);
        }
      } catch {
        // silently ignore
      }
    };

    void fetchCost();
    costIntervalRef.current = setInterval(fetchCost, 5000);

    return () => {
      cancelled = true;
      if (costIntervalRef.current) clearInterval(costIntervalRef.current);
    };
  }, [vmClient]);

  const userId = auth.user?.id;
  const authStatus = auth.loading ? 'loading…' : auth.authProvider ?? 'none';
  const tokenValues = sessionUsage
    ? {
        '': `${sessionUsage.input_tokens.toLocaleString()}↓ ${sessionUsage.output_tokens.toLocaleString()}↑`,
        'input (uncached)': sessionUsage.input_tokens - sessionUsage.cached_tokens,
        output: sessionUsage.output_tokens,
        total: sessionUsage.total_tokens,
        cached: sessionUsage.cached_tokens,
        'cache write': sessionUsage.cache_write_tokens,
        'ctx window': sessionUsage.context_window_tokens,
        'ctx cached': sessionUsage.context_cached_tokens,
        'ctx reason': sessionUsage.context_reasoning_tokens,
        inferences: sessionUsage.inference_count,
        'upstream $': sessionUsage.cost_details.upstream_inference_cost,
        'prompt $': sessionUsage.cost_details.upstream_inference_prompt_cost,
        'compl $': sessionUsage.cost_details.upstream_inference_completions_cost,
      }
    : null;

  const devValues: Record<string, any> = {
    Model: serverInfo.model,
    'Server Commit': serverInfo.commit,
    User: userId ?? null,
    Auth: authStatus,
    Env: env.environment,
    Cost: sessionUsage?.cost ?? null,
    Tokens: tokenValues,
    Balance: balanceLoading ? '…' : balanceError ? 'err' : remainingBalance ?? null,
  };

  const paper = usePaperThemeActive();
  const open = useDevPanelOpen();
  const [mode, setMode] = useState<WindowedCardMode>('maximized');

  // Legacy non-paper still uses the original bottom-left floater.
  if (!paper) {
    const legacyChrome =
      'rounded-lg border border-sky-500/20 bg-sky-950/70 px-3 py-2 text-sky-200/90 shadow-lg backdrop-blur-sm';
    const legacyPos =
      'fixed bottom-3 left-3 z-[10050] hidden max-w-xs font-mono text-[11px] leading-snug sm:block';
    return createPortal(
      <div className={legacyPos}>
        <div className={legacyChrome}>
          <DevValues values={devValues} />
          <ThemeTriggers />
        </div>
      </div>,
      document.body,
    );
  }

  // Paper mode: the rail toggle controls open/close; close docks the card.
  if (!open) return null;

  const minimizedContent = (
    <div className="flex flex-col gap-1">
      <ComponentTriggers />
      <VisualsTriggers />
    </div>
  );

  const maximizedContent = (
    <div className="flex flex-col">
      <DevValues values={devValues} />
      <ThemeTriggers />
      <VisualsTriggers />
      <SplitTriggers />
      <ComponentTriggers />
      <PaperPopupTriggers />
    </div>
  );

  return (
    <>
      {createPortal(
        <WindowedCard
          title="Developer Console"
          mode={mode}
          onModeChange={setMode}
          onClose={() => setDevPanelOpen(false)}
          minimizedContent={minimizedContent}
          maximizedContent={maximizedContent}
          positionClass="fixed left-1/2 bottom-3 -translate-x-1/2 z-[10050]"
        />,
        document.body,
      )}
      <DevComponentPreviewHost />
    </>
  );
};

interface VisualsTriggersProps {}

const BANNER_TRIGGERS: { key: DevBannerKey; label: string }[] = [
  { key: 'disconnect', label: 'Disconnect' },
  { key: 'powerOn', label: 'Power On' },
  { key: 'agentError', label: 'Agent Error' },
  { key: 'agentStarting', label: 'Starting…' },
  { key: 'agentStopping', label: 'Stopping…' },
  { key: 'poweredOff', label: 'Powered Off' },
  { key: 'demoSignIn', label: 'Demo Sign-in' },
  { key: 'anonSignIn', label: 'Anon Sign-in' },
  { key: 'vmBoot', label: 'VM Boot' },
  { key: 'workingBubble', label: 'Working Bubble' },
  { key: 'selfInitiatedTask', label: 'Self-init Task' },
  { key: 'railPill', label: 'Floating Rail Shape' },
  { key: 'forceArtifact', label: 'Force Panel' },
  { key: 'paymentRequired', label: 'Payment Required' },
  { key: 'paymentRequiredAnon', label: 'Payment Required (Anon)' },
  { key: 'chatErrorUnknown', label: 'Chat Error (Generic)' },
];

const VisualsTriggers = ({}: VisualsTriggersProps) => {
  const overrides = useDevBannerOverrides();
  return (
    <Minimizable
      summary={(m) => <Row label="Chat Window Visuals" value={m ? '…' : ''} minimized={m} />}
    >
      <div className="pl-3 pt-1 pb-0.5 flex flex-wrap gap-1">
        {BANNER_TRIGGERS.map(({ key, label }) => {
          const on = overrides[key];
          const base = DEV_TRIGGER_BUTTON_BASE;
          const cls = on
            ? `${base} border-[var(--periwinkle)] bg-[var(--periwinkle-veil)] text-[var(--fg)]`
            : `${base} border-[var(--hair)] text-[var(--fg-3)] hover:bg-[var(--periwinkle-wash)]`;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDevBannerOverride(key, !on)}
              className={cls}
            >
              {label}
            </button>
          );
        })}
      </div>
    </Minimizable>
  );
};

const SplitTriggers = () => {
  const ratio = useDevPanelSplit();
  const currentPanelPct = Math.round(ratio * 100);
  return (
    <Minimizable
      summary={(m) => (
        <Row
          label="ChatWindow/Canvas Split"
          value={m ? '…' : `${100 - currentPanelPct}/${currentPanelPct}`}
          minimized={m}
        />
      )}
    >
      <div className="pl-3 pt-1 pb-0.5 flex flex-wrap gap-1">
        {DEV_PANEL_SPLIT_PRESETS.map(({ chat, panel }) => {
          const on = panel === currentPanelPct;
          const base = DEV_TRIGGER_BUTTON_BASE;
          const cls = on
            ? `${base} border-[var(--periwinkle)] bg-[var(--periwinkle-veil)] text-[var(--fg)]`
            : `${base} border-[var(--hair)] text-[var(--fg-3)] hover:bg-[var(--periwinkle-wash)]`;
          return (
            <button
              key={panel}
              type="button"
              onClick={() => setDevPanelSplit(panel / 100)}
              className={cls}
            >
              {chat}/{panel}
            </button>
          );
        })}
      </div>
    </Minimizable>
  );
};

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'paper-auto', label: 'Auto' },
  { value: 'paper-dark', label: 'Paper Dark' },
  { value: 'paper-light', label: 'Paper Light' },
  { value: null, label: 'Default' },
];

const themeLabel = (v: ThemePreference) =>
  v === 'paper-dark'
    ? 'paper-dark'
    : v === 'paper-light'
      ? 'paper-light'
      : v === 'paper-auto'
        ? 'auto'
        : 'default';

const ThemeTriggers = () => {
  const current = usePaperThemePreference();
  const legacy = !isPaperThemeActive();
  return (
    <Minimizable
      summary={(m) => <Row label="Theme" value={m ? '…' : themeLabel(current)} minimized={m} />}
    >
      <div className="pl-3 pt-1 pb-0.5 flex flex-wrap gap-1">
        {THEME_OPTIONS.map(({ value, label }) => {
          const on = value === current;
          const base = DEV_TRIGGER_BUTTON_BASE;
          const cls = legacy
            ? on
              ? `${base} border-sky-400 bg-sky-500/20 text-sky-100`
              : `${base} border-sky-500/30 text-sky-200/80 hover:bg-sky-500/10`
            : on
              ? `${base} border-[var(--periwinkle)] bg-[var(--periwinkle-veil)] text-[var(--fg)]`
              : `${base} border-[var(--hair)] text-[var(--fg-3)] hover:bg-[var(--periwinkle-wash)]`;
          return (
            <button
              key={String(value)}
              type="button"
              onClick={() => setPaperTheme(value)}
              className={cls}
            >
              {label}
            </button>
          );
        })}
      </div>
    </Minimizable>
  );
};

const COMPONENT_TRIGGERS: { key: DevComponentPreviewKey; label: string }[] = [
  { key: 'authPending', label: 'OAuth pending' },
  { key: 'authSuccess', label: 'OAuth success' },
  { key: 'authError', label: 'OAuth error' },
];

const PAPER_POPUP_TRIGGERS: { key: DevComponentPreviewKey; label: string }[] = [
  { key: 'sonnerToast', label: 'Sonner toast' },
  { key: 'ephemeralBanner', label: 'Ephemeral banner' },
  { key: 'attachmentUploadError', label: 'Upload failed' },
  { key: 'attachmentValidationError', label: 'Invalid format' },
  { key: 'billingCancel', label: 'Billing cancel' },
  { key: 'billingChange', label: 'Billing change' },
  { key: 'agentRename', label: 'Agent rename' },
  { key: 'agentDelete', label: 'Agent delete' },
];

const ComponentTriggers = () => {
  const previews = useDevComponentPreview();
  return (
    <Minimizable
      summary={(m) => <Row label="Oauth Components" value={m ? '…' : ''} minimized={m} />}
    >
      <div className="pl-3 pt-1 pb-0.5 flex flex-wrap gap-1">
        {COMPONENT_TRIGGERS.map(({ key, label }) => {
          const on = previews[key];
          const base = DEV_TRIGGER_BUTTON_BASE;
          const cls = on
            ? `${base} border-[var(--periwinkle)] bg-[var(--periwinkle-veil)] text-[var(--fg)]`
            : `${base} border-[var(--hair)] text-[var(--fg-3)] hover:bg-[var(--periwinkle-wash)]`;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDevComponentPreview(key, !on)}
              className={cls}
            >
              {label}
            </button>
          );
        })}
      </div>
    </Minimizable>
  );
};

const PaperPopupTriggers = () => {
  const previews = useDevComponentPreview();
  return (
    <Minimizable
      summary={(m) => <Row label="Paper Popups" value={m ? '…' : ''} minimized={m} />}
    >
      <div className="pl-3 pt-1 pb-0.5 flex flex-wrap gap-1">
        {PAPER_POPUP_TRIGGERS.map(({ key, label }) => {
          const on = previews[key];
          const base = DEV_TRIGGER_BUTTON_BASE;
          const cls = on
            ? `${base} border-[var(--periwinkle)] bg-[var(--periwinkle-veil)] text-[var(--fg)]`
            : `${base} border-[var(--hair)] text-[var(--fg-3)] hover:bg-[var(--periwinkle-wash)]`;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDevComponentPreview(key, !on)}
              className={cls}
            >
              {label}
            </button>
          );
        })}
      </div>
    </Minimizable>
  );
};

interface MinimizableProps {
  summary: (minimized: boolean) => ReactNode;
  children: ReactNode;
  defaultMinimized?: boolean;
}

const Minimizable = ({ summary, children, defaultMinimized = true }: MinimizableProps) => {
  const [minimized, setMinimized] = useState(defaultMinimized);
  const hover = isPaperThemeActive()
    ? 'cursor-pointer hover:bg-[var(--periwinkle-wash)]'
    : 'cursor-pointer hover:bg-sky-800/30';
  return (
    <div>
      <div
        onClick={() => setMinimized((m) => !m)}
        className={hover}
      >
        {summary(minimized)}
      </div>
      {!minimized && children}
    </div>
  );
};

interface DevValuesProps {
  values: Record<string, any>;
}

const DevValues = ({ values }: DevValuesProps) => (
  <div>
    {Object.entries(values)
      .filter(([key]) => key !== '')
      .map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return (
            <Minimizable
              key={key}
              summary={(m) => <Row label={key} value={value['']} minimized={m} />}
            >
              <div className="pl-3">
                <DevValues values={value} />
              </div>
            </Minimizable>
          );
        }
        return <Row key={key} label={key} value={value} />;
      })}
  </div>
);

interface RowProps {
  label: string;
  value: unknown;
  minimized?: boolean;
}

const formatValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(4);
  }
  return String(value);
};

const Row = ({ label, value, minimized }: RowProps) => {
  const paper = isPaperThemeActive();
  const labelCls = paper
    ? 'shrink-0 min-w-28 pr-2 text-[var(--fg-3)] whitespace-nowrap'
    : 'shrink-0 min-w-28 pr-2 text-sky-400/60 whitespace-nowrap';
  const chevronCls = paper
    ? 'inline-block w-3 text-[var(--fg-4)]'
    : 'inline-block w-3 text-sky-400/40';
  const valueCls = paper ? 'text-[var(--fg)]' : 'text-sky-100/90';
  return (
    <div className="flex">
      <span className={labelCls}>
        {minimized != null && (
          <span className={chevronCls}>{minimized ? '▸' : '▾'}</span>
        )}
        {label}
      </span>
      <span className={valueCls}>{formatValue(value)}</span>
    </div>
  );
};
